import { Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import type { SelectableDashboardView } from "./DashboardViewSwitch";

export type EmptyStateKind =
  | { kind: "onboarding" }
  | { kind: "search-no-results"; search: string }
  | { kind: "view-empty"; view: SelectableDashboardView };

/**
 * `totalLeaguesUnfiltered` is the dashboard API's league count for the
 * current request. When a facility is selected that request is already
 * facility-scoped server-side, so zero there only means "none at this
 * facility", not "none anywhere" — `facilityFiltered` keeps that case out of
 * the first-visit onboarding page (which hides the toolbar, so the operator
 * could not even switch the facility back).
 */
export function resolveEmptyState(args: {
  totalLeaguesUnfiltered: number;
  currentViewFilteredCount: number;
  search: string;
  view: SelectableDashboardView;
  facilityFiltered?: boolean;
}): EmptyStateKind | null {
  const {
    totalLeaguesUnfiltered,
    currentViewFilteredCount,
    search,
    view,
    facilityFiltered = false,
  } = args;
  if (totalLeaguesUnfiltered === 0 && !facilityFiltered) return { kind: "onboarding" };
  if (currentViewFilteredCount > 0) return null;
  if (search.trim() !== "") {
    return { kind: "search-no-results", search };
  }
  return { kind: "view-empty", view };
}

const VIEW_EMPTY_COPY: Record<SelectableDashboardView, { title: string; description: string }> = {
  IN_SEASON: {
    title: "No leagues in season",
    description: "Leagues show here once their season start date arrives.",
  },
  UPCOMING: {
    title: "No upcoming leagues",
    description:
      "Leagues show here until their season starts, including drafts and finalized leagues.",
  },
  FINISHED: {
    title: "No finished leagues",
    description: "Leagues show here once every round is finalized.",
  },
};

const ONBOARDING_STEPS: { title: string; description: string }[] = [
  {
    title: "Choose a format",
    description: "Stroke play, match play, points or scramble, for individuals or teams.",
  },
  {
    title: "Set the season",
    description: "Pick the start date, the number of rounds and when each round can be played.",
  },
  {
    title: "Add players",
    description: "Add golfers to the league. Scores flow in once they play on the simulator.",
  },
];

export function LeagueDashboardEmptyState({
  state,
  onNewLeague,
  onClearSearch,
}: {
  state: EmptyStateKind;
  onNewLeague: () => void;
  onClearSearch: () => void;
}) {
  if (state.kind === "onboarding") {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Trophy />
          </EmptyMedia>
          <EmptyTitle>Create your first league</EmptyTitle>
          <EmptyDescription>
            Pick a format, choose a course and add your players. Scores flow in from
            the simulator, and standings update after every round.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent className="flex-row justify-center gap-2">
          <Button size="sm" className="rounded-lg text-xs" onClick={onNewLeague}>
            New League
          </Button>
        </EmptyContent>
        <ol className="mt-4 grid w-full max-w-3xl grid-cols-1 gap-3 text-left sm:grid-cols-3">
          {ONBOARDING_STEPS.map((step, i) => (
            <li
              key={step.title}
              className="rounded-xl border border-border bg-surface p-4"
            >
              <div className="poppins text-xs font-bold uppercase tracking-wide text-text-faint">
                Step {i + 1}
              </div>
              <div className="poppins mt-1 text-sm font-semibold text-ink">
                {step.title}
              </div>
              <p className="mulish mt-1 text-xs text-text-muted">
                {step.description}
              </p>
            </li>
          ))}
        </ol>
      </Empty>
    );
  }

  if (state.kind === "search-no-results") {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>No leagues match &quot;{state.search}&quot;.</EmptyTitle>
        </EmptyHeader>
        <EmptyContent className="flex-row justify-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="rounded-lg text-xs"
            onClick={onClearSearch}
          >
            Clear search
          </Button>
        </EmptyContent>
      </Empty>
    );
  }

  const copy = VIEW_EMPTY_COPY[state.view];
  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle>{copy.title}</EmptyTitle>
        <EmptyDescription>{copy.description}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent className="flex-row justify-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="rounded-lg text-xs"
          onClick={onNewLeague}
        >
          New League
        </Button>
      </EmptyContent>
    </Empty>
  );
}

export function LeagueDashboardLoadingSkeleton() {
  return (
    <div className="flex flex-col gap-3 p-6">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-16 w-full rounded-xl" />
      ))}
    </div>
  );
}

export function LeagueDashboardErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center">
      <p className="mulish text-sm text-text-muted">Leagues could not load.</p>
      <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
